import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  RefreshCw, Loader2, ShieldCheck, AlertCircle, CheckCircle2,
  Clock, FileSearch,
} from "lucide-react";

/**
 * MOTWorkflowControlCard — Stage 1 surface.
 *
 * Surfaces the "Check DVSA MOT Result" button and the current workflow
 * status to the FOH. Reads from `/api/intake/sessions/{id}/mot-workflow`,
 * mutates via `POST .../check-dvsa`.
 *
 * Operational principle: the MOT tester does NOTHING in Yestimate. The
 * FOH clicks this single button once the tester confirms the MOT is
 * done on the DVSA portal — Yestimate fetches the result, parses
 * defects, creates pricing draft items tagged `source: "dvsa_mot_result"`,
 * and advances the workflow status.
 *
 * Boundary discipline: for MOT-only journeys, imported items are labelled
 * "MOT Result Items" — NEVER Safety & Reliability Inspection findings.
 *
 * Props:
 *   sessionId         — intake session id (required)
 *   onImported        — callback fired after a successful import, so the
 *                       parent page can re-fetch the outcome/defects list
 */
export default function MOTWorkflowControlCard({ sessionId, onImported }) {
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [lastResultMessage, setLastResultMessage] = useState(null);
  const [lastResultKind, setLastResultKind] = useState(null);
  const [error, setError] = useState(null);
  // Stage 2 — slot scheduler + manual-resolve state. Hooks declared here
  // (BEFORE any early-return) so they fire on every render in the same order.
  const [slotDraft, setSlotDraft] = useState("");
  const [durationDraft, setDurationDraft] = useState(45);
  const [savingSlot, setSavingSlot] = useState(false);
  const [manualNote, setManualNote] = useState("");
  const [resolvingManual, setResolvingManual] = useState(false);
  // Iter180 — External MOT import state.
  const [importingExternal, setImportingExternal] = useState(false);
  const [externalReason, setExternalReason] = useState("");

  const loadWorkflow = async () => {
    try {
      const r = await axios.get(`${API}/intake/sessions/${sessionId}/mot-workflow`);
      setWorkflow(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load MOT workflow");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) loadWorkflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Sync the slot draft inputs to the freshly-loaded workflow values
  // whenever the parent re-fetches. Declared at the top alongside the
  // load-workflow effect so hook order stays consistent in every render
  // — never below an early return.
  useEffect(() => {
    const iso = workflow?.mot_slot_time;
    const duration = workflow?.expected_duration_minutes || 45;
    if (!iso) {
      setSlotDraft("");
    } else {
      try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        setSlotDraft(
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
        );
      } catch {
        setSlotDraft("");
      }
    }
    setDurationDraft(duration);
  }, [workflow?.mot_slot_time, workflow?.expected_duration_minutes]);

  const handleCheck = async () => {
    setChecking(true);
    setLastResultMessage(null);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/intake/sessions/${sessionId}/mot-workflow/check-dvsa`,
      );
      const outcome = r.data?.result?.outcome;
      const msg = r.data?.result?.message;
      setLastResultKind(outcome);
      setLastResultMessage(msg);
      setWorkflow(r.data?.workflow);
      if (outcome === "imported" && onImported) {
        onImported(r.data.result);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "DVSA check failed");
    } finally {
      setChecking(false);
    }
  };

  if (!sessionId) return null;

  if (loading) {
    return (
      <section
        className="rounded-lg border border-slate-200 bg-white px-5 py-4"
        data-testid="mot-workflow-card-loading"
      >
        <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading MOT workflow…
        </p>
      </section>
    );
  }

  const status = workflow?.status || "MOT_BOOKED";
  const statusLabel = workflow?.status_label || "MOT booked";
  const baseline = workflow?.baseline;
  const palette = statusPalette(status);
  const lastChecked = workflow?.last_checked_at;
  const slotTime = workflow?.mot_slot_time || "";
  const pollAttempts = workflow?.poll_attempts || 0;
  const pollCap = workflow?.hard_stop_max_attempts || 12;
  const isManualReview = status === "MOT_RESULT_PENDING_MANUAL_REVIEW";

  const handleSaveSlot = async () => {
    if (!slotDraft) return;
    setSavingSlot(true);
    setError(null);
    try {
      // Send as local datetime — backend parses with timezone awareness.
      const iso = new Date(slotDraft).toISOString();
      const r = await axios.post(
        `${API}/intake/sessions/${sessionId}/mot-workflow/slot`,
        { mot_slot_time: iso, expected_duration_minutes: Number(durationDraft) || 45 },
      );
      setWorkflow(r.data?.workflow);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save the MOT slot");
    } finally {
      setSavingSlot(false);
    }
  };

  const handleManualResolve = async () => {
    setResolvingManual(true);
    setError(null);
    try {
      await axios.post(
        `${API}/intake/sessions/${sessionId}/mot-workflow/manual-resolve`,
        { note: manualNote.trim() || "Resolved manually by FOH" },
      );
      setManualNote("");
      await loadWorkflow();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not resolve manually");
    } finally {
      setResolvingManual(false);
    }
  };

  // Iter180 — External MOT import. When the baseline carries a recent
  // (within 14 days) completed test AND no outcome has been imported
  // yet, surface a one-tap "Import as outcome" path. Same backend that
  // handles the "tester completed it elsewhere, customer is here for
  // repairs" scenario.
  const handleImportExternalMot = async () => {
    setImportingExternal(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/intake/sessions/${sessionId}/mot-workflow/import-baseline-as-outcome`,
        { reason: externalReason.trim() || null },
      );
      const outcome = r.data?.result?.outcome;
      const msg = r.data?.result?.message;
      setLastResultKind(outcome === "already_imported" ? "imported" : outcome);
      setLastResultMessage(msg);
      setWorkflow(r.data?.workflow);
      setExternalReason("");
      if ((outcome === "imported" || outcome === "already_imported") && onImported) {
        onImported(r.data.result);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not import external MOT");
    } finally {
      setImportingExternal(false);
    }
  };

  // Visible whenever DVSA has a test on record AND we haven't imported
  // it yet. Recency check uses completed_date if available (we accept
  // anything within the last 14 days as "recent enough" — older tests
  // are typically the previous year's MOT and shouldn't be re-imported
  // as the current outcome).
  const baselineForImport = workflow?.baseline;
  const hasOutcomeAlready = !!workflow?.imported_outcome_id;
  const externalMotEligible = (() => {
    if (hasOutcomeAlready) return false;
    if (!baselineForImport?.test_number) return false;
    if (!baselineForImport?.completed_date) return true;  // assume recent
    try {
      const d = new Date(baselineForImport.completed_date);
      const ageDays = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
      return ageDays <= 14;
    } catch {
      return true;
    }
  })();

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="mot-workflow-card"
      data-mot-workflow-status={status}
    >
      <header className="px-5 pt-3.5 pb-2.5 flex items-center justify-between gap-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-teal-600" />
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            MOT workflow
          </p>
        </div>
        <span
          className={`text-[11px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full ${palette.badge}`}
          data-testid="mot-workflow-status-badge"
        >
          {statusLabel}
        </span>
      </header>

      <div className="px-5 py-4 space-y-3">
        {/* Baseline ----------------------------------------------------- */}
        {baseline ? (
          <div className="text-[12.5px] text-slate-600 leading-snug">
            <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-1">
              Baseline MOT on file
            </p>
            <p data-testid="mot-workflow-baseline">
              {baseline.test_number ? (
                <>
                  Test{" "}
                  <span className="font-mono text-slate-700">
                    #{baseline.test_number}
                  </span>{" "}
                  · {baseline.test_result || "—"}
                  {baseline.expiry_date && (
                    <> · expires {formatDate(baseline.expiry_date)}</>
                  )}
                </>
              ) : (
                <span className="italic text-slate-500">
                  No prior MOT on DVSA record.
                </span>
              )}
            </p>
          </div>
        ) : (
          <p
            className="text-[12.5px] text-slate-500 italic leading-snug"
            data-testid="mot-workflow-no-baseline"
          >
            Baseline will be captured the first time you check DVSA.
          </p>
        )}

        {/* Slot scheduler — Stage 2 background poller gate ------------- */}
        <div
          className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5"
          data-testid="mot-workflow-slot-scheduler"
        >
          <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">
            MOT slot
            <span className="ml-2 italic text-[10px] normal-case tracking-normal text-slate-400">
              We start auto-polling DVSA at slot time
            </span>
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="block text-[11px] text-slate-600 mb-0.5">Start time</label>
              <input
                type="datetime-local"
                value={slotDraft}
                onChange={(e) => setSlotDraft(e.target.value)}
                className="w-full text-[12.5px] border border-slate-300 rounded px-2 py-1.5"
                data-testid="mot-workflow-slot-time-input"
              />
            </div>
            <div>
              <label className="block text-[11px] text-slate-600 mb-0.5">Duration (m)</label>
              <input
                type="number"
                min={5}
                max={480}
                value={durationDraft}
                onChange={(e) => setDurationDraft(e.target.value)}
                className="w-full text-[12.5px] border border-slate-300 rounded px-2 py-1.5"
                data-testid="mot-workflow-slot-duration-input"
              />
            </div>
          </div>
          <Button
            onClick={handleSaveSlot}
            disabled={!slotDraft || savingSlot}
            size="sm"
            variant="outline"
            className="mt-2 text-[12px]"
            data-testid="mot-workflow-slot-save-btn"
          >
            {savingSlot ? "Saving…" : (slotTime ? "Update slot" : "Schedule slot")}
          </Button>
          {pollAttempts > 0 && (
            <p
              className="mt-2 text-[11px] text-slate-500 italic"
              data-testid="mot-workflow-poll-attempts"
            >
              Auto-poll attempts: {pollAttempts} / {pollCap}
            </p>
          )}
        </div>

        {/* Iter180 — External MOT import card ------------------------- */}
        {externalMotEligible && (
          <div
            className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2.5 space-y-2"
            data-testid="mot-workflow-external-import"
          >
            <div className="flex items-start gap-2">
              <FileSearch className="w-4 h-4 text-sky-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-sky-900">
                  External MOT detected
                </p>
                <p className="text-[12px] text-sky-800 leading-snug mt-0.5">
                  DVSA has a recent test on file
                  {baselineForImport?.completed_date && (
                    <> ({formatDate(baselineForImport.completed_date)})</>
                  )}
                  . If the customer had this MOT done elsewhere and is here for
                  repairs, import it directly — no need to wait for a new test.
                </p>
              </div>
            </div>
            <textarea
              value={externalReason}
              onChange={(e) => setExternalReason(e.target.value)}
              placeholder="Optional note (e.g. 'MOT at Garage X, customer here for advisories')"
              rows={2}
              className="w-full text-[12.5px] border border-sky-300 rounded px-2 py-1.5"
              data-testid="mot-workflow-external-reason"
            />
            <Button
              size="sm"
              onClick={handleImportExternalMot}
              disabled={importingExternal}
              className="bg-sky-700 hover:bg-sky-800 text-white text-[12px]"
              data-testid="mot-workflow-external-import-btn"
            >
              {importingExternal ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Importing…</>
              ) : (
                "Import this MOT as our outcome"
              )}
            </Button>
          </div>
        )}

        {/* Manual review banner — appears when the hard-stop has fired -- */}
        {isManualReview && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 space-y-2"
            data-testid="mot-workflow-manual-review-banner"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[13px] font-semibold text-amber-900">
                  Manual review needed
                </p>
                <p className="text-[12px] text-amber-800 leading-snug mt-0.5">
                  The background poller stopped without seeing a new DVSA
                  result. Confirm the MOT with the tester directly and add
                  a short note before resolving.
                </p>
              </div>
            </div>
            <textarea
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
              placeholder="Short note (e.g. confirmed by tester, paperwork still being filed)"
              rows={2}
              className="w-full text-[12.5px] border border-amber-300 rounded px-2 py-1.5"
              data-testid="mot-workflow-manual-resolve-note"
            />
            <Button
              size="sm"
              onClick={handleManualResolve}
              disabled={resolvingManual}
              className="bg-amber-700 hover:bg-amber-800 text-white text-[12px]"
              data-testid="mot-workflow-manual-resolve-btn"
            >
              {resolvingManual ? "Resolving…" : "Resolve manually"}
            </Button>
          </div>
        )}

        {/* Primary CTA -------------------------------------------------- */}
        <div className="pt-1">
          <Button
            onClick={handleCheck}
            disabled={checking}
            className="w-full bg-teal-700 hover:bg-teal-800 text-white text-[14px] font-semibold h-11"
            data-testid="mot-workflow-check-btn"
          >
            {checking ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Checking DVSA…
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Check DVSA MOT Result
              </>
            )}
          </Button>
          {lastChecked && !checking && (
            <p
              className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1"
              data-testid="mot-workflow-last-checked"
            >
              <Clock className="w-3 h-3" />
              Last checked {formatRelative(lastChecked)}
            </p>
          )}
        </div>

        {/* Result feedback --------------------------------------------- */}
        {lastResultKind === "imported" && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5"
            data-testid="mot-workflow-result-imported"
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[13px] font-semibold text-emerald-900">
                  MOT Result Items imported
                </p>
                <p className="text-[12px] text-emerald-800 leading-snug mt-0.5">
                  {lastResultMessage} — they're now on the MOT outcome
                  capture form below, ready for FOH pricing.
                </p>
              </div>
            </div>
          </div>
        )}

        {lastResultKind === "no_new_result" && (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5"
            data-testid="mot-workflow-result-none"
          >
            <div className="flex items-start gap-2">
              <FileSearch className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-[13px] text-slate-700 leading-snug">
                {lastResultMessage} DVSA may not have published the new
                test yet — try again in a few minutes.
              </p>
            </div>
          </div>
        )}

        {lastResultKind === "dvsa_error" && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5"
            data-testid="mot-workflow-result-error"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-[13px] text-amber-900 leading-snug">
                DVSA returned an error — {lastResultMessage || "try again in a moment."}
              </p>
            </div>
          </div>
        )}

        {error && (
          <p
            className="text-[12.5px] text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 flex items-start gap-1.5"
            data-testid="mot-workflow-error"
          >
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function statusPalette(status) {
  switch (status) {
    case "PRICING_READY":
    case "DVSA_RESULT_IMPORTED":
      return { badge: "bg-emerald-100 text-emerald-800" };
    case "AWAITING_DVSA_RESULT":
      return { badge: "bg-blue-100 text-blue-800" };
    case "AWAITING_MOT_TEST":
      return { badge: "bg-amber-100 text-amber-800" };
    case "MOT_RESULT_PENDING_MANUAL_REVIEW":
      return { badge: "bg-amber-200 text-amber-900" };
    case "CUSTOMER_CONTACT_REQUIRED":
      return { badge: "bg-orange-100 text-orange-800" };
    case "COMPLETE":
      return { badge: "bg-slate-200 text-slate-700" };
    case "MOT_BOOKED":
    default:
      return { badge: "bg-slate-100 text-slate-700" };
  }
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
    return new Date(iso).toLocaleString("en-GB");
  } catch {
    return iso;
  }
}
